import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import { type IconButtonProps } from '@atlaskit/button/new';
import { type Keymap } from '@atlaskit/editor-common/keymaps';
import type { Fragment, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';
import type { MentionMap } from '@atlassian/ai-model-io/utils/mention-map';
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import { type EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import type { BackendModel, IntentSchemaId } from '../../../types/prompt-request-types';
import type { EditorPluginAIProvider, RovoPublish } from '../../../types/types';
import type {
	AIExperienceMachineContextForRequest,
	ConvoAiFeatureId,
	RequestOptions,
	ResponseObject,
} from '../../utils/request/types';
import type { Positions } from '../../utils/xstate/get-ai-experience-service';

import { type CONFIG_ITEM_KEYS } from './config-item-keys';
import type { TranslationLanguageLabels } from './prompts/translate/types';

export type ActionAppearance = 'primary' | 'secondary' | 'cancel';

export type StatusLozengeType = 'new' | 'beta';

export type SelectionType = 'range' | 'empty';

/**
 * This is a map of ids to node details, which is used when mapping markdown back to pm.
 */
export type UpdateIdMap = (_: { idMap: IDMap }) => void;

export type EditorPluginAIConfigItemMarkdownAction = {
	appearance: ActionAppearance;
	getDisabledState?: ({
		markdown,
		formatMessage,
	}: {
		formatMessage: IntlShape['formatMessage'];
		markdown: string;
	}) => {
		isDisabled: boolean;
		tooltip?: string;
	};
	icon?: IconButtonProps['icon'];
	isRovoAgentAction?: boolean;
	// Note: as we flesh out the experience -- we'll likely want
	// to either have actions return a Transaction -- or some other type using
	// utils we provide.
	run: (data: {
		editorView: EditorView;
		markdown: string;
		pmFragment: Fragment;
		positions: Positions;
		selectionMetaData?: {
			sliceOpenEnd?: number;
			sliceOpenStart?: number;
		};
		triggeredFor?: NodeType;
	}) => void;
	/**
	 * Title of action which is presented on the preview screen.
	 */
	title: MessageDescriptor;
	type: 'markdown';
};

export type EditorPluginAIConfigItemAgentAction = {
	appearance: ActionAppearance;
	// Used when action buttons layout is in 'compact' mode
	icon: IconButtonProps['icon'];
	// Whether to preserve the AI highlight pseudo selection as
	// a true ProseMirror editor selection after the action function
	// executes
	preserveEditorSelectionOnComplete?: boolean;
	run: (data: { channelId?: string; publish?: RovoPublish }) => void;
	/** An optional shortcut keymap for action. */
	shortcut?: Keymap;
	title: MessageDescriptor;
	type: 'agent';
};

type Dialogue = { content: string };

export type EditorPluginAIConfigItemGenericChatAction = Omit<
	EditorPluginAIConfigItemAgentAction,
	'run' | 'type'
> & {
	run: (data: {
		dialogues: Array<{ agent_message: Dialogue; human_message: Dialogue }>;
		name: string;
		publish?: RovoPublish;
	}) => void;
	type: 'generic-chat';
};

export type EditorPluginAIConfigItemAction =
	| EditorPluginAIConfigItemMarkdownAction
	| EditorPluginAIConfigItemAgentAction
	| EditorPluginAIConfigItemGenericChatAction;

export type ConfigItemInitialContext<S extends SelectionType = SelectionType> = S extends 'range'
	? InitialContextSelectionRange
	: InitialContextSelectionEmpty;

export type GetInitialContext = ({
	editorView,
	positions,
	intl,
}: {
	additionalIntentSchemaIds?: IntentSchemaId[];
	editorView: EditorView;
	intentSchemaId?: IntentSchemaId;
	intl: IntlShape;
	mentionMap?: MentionMap;
	positions: Positions;
	updateIdMap: UpdateIdMap;
}) => ConfigItemInitialContext<SelectionType>;

/** A context from analytics. */
export type AnalyticsContext = {
	/**
	 * The unique identifier which links multiple AI experiences together.
	 */
	aiGroupId: string;
	/**
	 * The unique identifier for the current user journey.
	 *
	 * This identifier is used to track the user journey in the analytics flow.
	 */
	aiSessionId: string;
};

export type InitialContextSelectionEmpty = {
	contentStatistics?: ContentStatistics;
	document: string;
	intentSchemaId: IntentSchemaId;
	// make reading selection easier for typescript
	selection?: undefined;
	targetTranslationLanguage?: TranslationLanguageLabels;
	userLocale: string;
};

type InitialContextSelectionRange = {
	contentStatistics?: ContentStatistics;
	document?: string;
	intentSchemaId: IntentSchemaId;
	selection: string;
	targetTranslationLanguage?: TranslationLanguageLabels;
	userLocale: string;
};

/**
 * type guard to detect if the initial context is for range selection
 * based on selection prop availability
 */
export function isInitialContextSelectionRange(
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	value: ConfigItemInitialContext<any>,
): value is InitialContextSelectionRange {
	return 'selection' in value && typeof value.selection === 'string';
}

/**
 * `EditorPluginAIConfigItem`s are used by EditorPluginAI to provide AI experiences.
 */
export type EditorPluginAIConfigItem = {
	actions: {
		/**
		 * List of actions which can be taken with the prompt request response when there is no selection
		 */
		empty?: EditorPluginAIConfigItemAction[];
		/**
		 * List of actions which can be taken with the prompt request response when there is a selection
		 */
		selection?: EditorPluginAIConfigItemAction[];
	};

	/**
	 * When enabled, communication with backend will be done in ADF format.
	 */
	adfPrompt?: boolean;

	/**
	 * The Rovo Agent associated with this config item.
	 */
	agent?: EditorAgent;

	/**
	 * The agent id to be used for the config item
	 */
	agentId?: string;

	/**
	 * always need doc for the config item
	 */
	alwaysNeedDoc?: boolean;

	/**
	 * always need no doc and selection for the config item
	 */
	alwaysNoDocAndSelection?: boolean;

	/**
	 * This is used to determine if the diff preview can be toggled for this config item. If this is falsey,
	 * the diff preview will not be shown for this config item.
	 */
	canToggleDiffPreview?: boolean;

	/**
	 * This is used to determine if the user has the ability to toggle the page knowledge
	 * for free generate prompts which will send the full document in the request
	 */
	canTogglePageKnowledge?: boolean;

	/** Appears in;
	 * - when the user searches for ai actions and gets a list (quick insert)
	 */
	description: MessageDescriptor;

	/**
	 * This option allows the product to hide interrogation flow on the preview screen per config item
	 */
	disableInterrogation?: () => boolean;

	/**
	 * By default, the whole document is highlighted when the selection is empty.
	 * You can turn this off by setting this to true.
	 */
	doNotHighlightDocWhenSelectionIsEmpty?: boolean;

	/**
	 * The endpoint to be used for the config item
	 */
	endpoint?: string;

	/**
	 * The experience id to be used for the config item
	 */
	experienceId?: string;

	/**
	 * This is the feature id used to identify the config item in the backend.
	 *
	 * @see https://bitbucket.org/atlassian/conversational-ai-platform/src/3f0f1434d6d1d66796ac3905adffad913917f08a/client/python/convo_ai_client/convo_ai_client/models/editor_ai_feature_request_feature.py
	 */
	featureId?: ConvoAiFeatureId;

	/**
	 * BackendModel to be used for the config item
	 * Useful for running experiments to test different models
	 *
	 * To be passed into triggerPromptRequest - but placed out here to be accessed easily
	 * for other uses e.g. Feedback Collection
	 *
	 * isInterrogate is only passed in so that the interrogate prompt can be
	 * differentiated for turning on / off backend experiments
	 */
	getBackendModel?: (isInterrogate?: boolean) => BackendModel;

	getCustomHeaders?: () => Record<string, string>;

	/**
	 * request payload for the config item, used for
	 * - Jira Improve description
	 */
	getRequestPayload?: (_: {
		aiSessionId?: string;
		doc?: string;
		isEmpty: boolean;
		locale: string;
		selection?: string;
	}) => Record<string, unknown>;

	handleFetchResponse?: (
		response: Response,
		requestOptions: RequestOptions,
		callback: (event: ResponseObject) => void,
		cacheKey: string,
	) => void;

	/**
	 * optional icon which is shown;
	 * - in quick insert listing
	 * - and on review screen
	 * - prompt suggestions list
	 */
	icon?: ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => React.ReactElement;

	/**
	 * By default, the whole document is replaced with the AI response when the selection is empty.
	 * By setting this to true, it will insert the AI response at the current position instead.
	 * This is useful for config items that generate content from scratch, such as "Free generate".
	 */
	insertOnEmptySelection?: boolean;

	/**
	 * The intent schema id to be used for the config item
	 * latestPromptResponse is from AIExperienceMachineContext
	 */
	intentSchemaId: IntentSchemaId;

	// The key is used to identify the config item type and will be send to identify the item in analytics
	key: CONFIG_ITEM_KEYS;

	/**
	 * for translate prompts, the language to translate to
	 */
	lang?: string;

	/**
	 * Used in the generative-ai-modal to group related config items under a single config item
	 */
	nestingConfig?: {
		icon?: ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => React.ReactElement;
		id: string;
		parentTestId?: string;
		parentTitle: MessageDescriptor;
		shortTitle: MessageDescriptor;
	};

	/**
	 * The prompt hint is used on the custom prompt screen
	 * to guide the user to expected prompt content.
	 * ie. for a promptLabel of "brainstorm" the promptHint
	 * would be "Tell me the topic...""
	 */
	promptHint?: MessageDescriptor;

	/**
	 * This label is presented in the custom prompt form,
	 * as a prefix to the prompt input.
	 * If not present -- no user input prompt is collected
	 * for the prompt, and we move it straight into loading
	 * with any initial context it is set up to collect.
	 * (i.e. the 'Brainstorm' refinement tag)
	 */
	promptLabel?: MessageDescriptor;

	// Quick insert priority
	quickInsertPriority?: number;

	/**
	 * When enabled, the config item will require textual content in selection.
	 */
	requiresTextualContent?: boolean;

	/**
	 * the pre-configured response for the config item, used for
	 * - Elevate free-generate
	 */
	response?: ResponseObject;

	/**
	 * If `true`, the config item will be shown in the Refine dropdown in the Preview screen.
	 */
	showInRefineDropdown?: boolean;

	/**
	 * This option allows use of Rovo agent as normal AI prompt.
	 * When this option is set to true,
	 * - agent starter will not be shown
	 * - AI prompt will be be shwown in "Actions" section instead of "Rovo agents"
	 */
	silentAgentPrompt?: boolean;

	/**
	 * This option allows the product to dictate if the config item is new or in beta, or other new status
	 */
	statusLozengeType?: StatusLozengeType;

	/** Appears in;
	 * - when the user searches for ai actions and gets a list (quick insert)
	 * - when the user highlights content and gets a list of ai actions (highlight toolbar)
	 * - when reviewing the generated content
	 */
	title: MessageDescriptor;

	/**
	 * This is a hook which provides the config item a way to mutate itself based on the current context.
	 */
	withContextMutation?: (
		configItem: EditorPluginAIConfigItem,
		context: AIExperienceMachineContextForRequest,
	) => EditorPluginAIConfigItem;
};

type EditorPluginAIConfigItemEmptyTrigger = {
	/**
	 * when empty is enabled, this can be used to specify the max size of the document
	 */
	docMaxSize?: number;
	/**
	 * when empty is enabled, this can be used to specify the min size of the document.
	 * default is 0, which means when there is no content, the config item will be shown
	 */
	docMinSize?: number;
	/**
	 * whether the prompt can be shown when there is no selection
	 */
	enabled: boolean;
};

type EditorPluginAIConfigItemSelectionTrigger = {
	/**
	 * whether the prompt can be shown when there is a selection
	 */
	enabled: boolean;
	/**
	 * when selection is true, this can be used to specify the max size of the selection
	 */
	selectionMaxSize?: number;
	/**
	 * when selection is true, this can be used to specify the min size of the selection
	 */
	selectionMinSize?: number;
};

type EditorPluginAIConfigItemTriggers = {
	/**
	 * custom canShow condition for the config item. If provided, will be used to determine if the config item should be shown
	 * return true if the config item should be shown, false otherwise
	 */
	canShow?: (_: { docSize: number; editorView: EditorView; selectionSize: number }) => boolean;

	/**
	 * whether the prompt can be shown when there is no selection
	 */
	empty: EditorPluginAIConfigItemEmptyTrigger;

	/**
	 * whether the prompt can be shown on specific nodes
	 */
	// nodes?: string[];

	/**
	 * whether the prompt can be shown when there is a selection
	 */
	selection: EditorPluginAIConfigItemSelectionTrigger;
};

export type EditorPluginAIConfigItemOptions = {
	// we can add other options for the config item here in the future
	triggers: EditorPluginAIConfigItemTriggers;
};

export type EditorPluginAIConfigItemWithOptions = {
	config: EditorPluginAIConfigItem;
} & EditorPluginAIConfigItemOptions;

export function isEditorPluginAIConfigItemWithOptions(
	item: EditorPluginAIConfigItemWithOptions | null | undefined | false,
): item is EditorPluginAIConfigItemWithOptions {
	return !!item;
}

export type EditorPluginAIConfigItemFactoryOptions = {
	isInDocumentStreamingEnabled?: EditorPluginAIProvider['isInDocumentStreamingEnabled'];
	product?: EditorPluginAIProvider['product'];
};

export type EditorPluginAIConfigItemFactory = (
	options?: EditorPluginAIConfigItemFactoryOptions,
) => EditorPluginAIConfigItem;

export type EditorPluginAIConfigItemWithOptionsFactory = (
	options?: EditorPluginAIConfigItemFactoryOptions,
) => EditorPluginAIConfigItemWithOptions;

export function canShowEditorPluginAIConfigItem({
	editorView,
	positions,
	isEmpty,
	item,
}: {
	editorView: EditorView;
	isEmpty: boolean;
	item: EditorPluginAIConfigItemWithOptions;
	positions: Positions;
}): boolean {
	const { triggers } = item;

	const doc = editorView.state.doc;
	// Size of the doc with a single empty paragraph is 4
	const docSize = doc.nodeSize - 4;
	const selectionSize = positions[1] - positions[0];

	if (triggers.canShow && !triggers.canShow({ editorView, docSize, selectionSize })) {
		return false;
	}

	const emptyOptions = triggers.empty;
	const emptyEnabled = emptyOptions.enabled;
	const docMinSize = emptyOptions.docMinSize ?? 0;
	const docMaxSize = emptyOptions.docMaxSize ?? Number.MAX_SAFE_INTEGER;

	const selectionOptions = triggers.selection;
	const selectionEnabled = selectionOptions.enabled;
	const selectionMinSize = selectionOptions.selectionMinSize ?? 1;
	const selectionMaxSize = selectionOptions.selectionMaxSize ?? Number.MAX_SAFE_INTEGER;

	if (isEmpty) {
		if (!emptyEnabled || docSize < docMinSize || docSize > docMaxSize) {
			return false;
		}
	} else {
		if (!selectionEnabled || selectionSize < selectionMinSize || selectionSize > selectionMaxSize) {
			return false;
		}
	}

	return true;
}

export function getEditorPluginAIConfigItemActions({
	isEmpty,
	configItem,
}: {
	configItem?: EditorPluginAIConfigItem;
	isEmpty: boolean;
}): EditorPluginAIConfigItemAction[] {
	if (!configItem) {
		return [];
	}

	const actions = (isEmpty ? configItem.actions.empty : configItem.actions.selection) ?? [];
	return actions;
}

export function getVisibleEditorPluginAIConfigItems({
	configItemWithOptions,
	editorView,
	positions,
	isEmpty,
}: {
	configItemWithOptions: EditorPluginAIConfigItemWithOptions[];
	editorView: EditorView;
	isEmpty: boolean;
	positions: Positions;
}): EditorPluginAIConfigItem[] {
	const arr: EditorPluginAIConfigItem[] = [];

	configItemWithOptions.forEach((item) => {
		if (!item) {
			return;
		}

		if (
			canShowEditorPluginAIConfigItem({
				editorView,
				positions,
				isEmpty,
				item,
			})
		) {
			arr.push(item.config);
		}
	});

	return arr;
}

/**
 * This function is used to get all the config items from the configItems array without checking if they can be shown
 */
export function getAllEditorPluginAIConfigItems({
	configItemWithOptions,
}: {
	configItemWithOptions: EditorPluginAIConfigItemWithOptions[];
}): EditorPluginAIConfigItem[] {
	const arr: EditorPluginAIConfigItem[] = [];

	configItemWithOptions.forEach((item) => {
		if (!item) {
			return;
		}
		arr.push(item.config);
	});

	return arr;
}

export function getTranslatedItemTitle(
	configItem: EditorPluginAIConfigItem,
	formatMessage: IntlShape['formatMessage'],
): string {
	// for agents return the saved agent name in the same language as it was saved
	if (configItem.agent) {
		const presetTitle = `@${configItem.agent.name}`;
		return presetTitle || formatMessage(configItem.title);
	}

	// adding beta label for the translation prompts
	if (configItem.key.startsWith('Translate to')) {
		return formatMessage(configItem.title);
	}

	// for all other items return the i18n-ed title
	return formatMessage(configItem.title);
}

/**
 * use this function get the translated prompt label in the ui components
 */
export function getTranslatedPromptLabel(
	configItem: EditorPluginAIConfigItem,
	formatMessage: IntlShape['formatMessage'],
) {
	/**
	 * When the config item is silently using agent behind the scenes for prompt
	 * then don't show the prompt label
	 */
	if (!configItem.silentAgentPrompt && configItem.agent) {
		// for agents return the saved agent name in the same language as it was saved
		return `@${configItem.agent.name}`;
	}

	// intentionally fall back to undefined as this value is checked to verify if we need to show the prompt input
	return configItem.promptLabel ? formatMessage(configItem.promptLabel) : undefined;
}
